import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Save, Trash2, Plus, Newspaper, Loader2, ExternalLink, X, Sparkles, Globe,
  CheckCircle2, XCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';

const EMPTY_POST = {
  id: null,
  title: '',
  slug: '',
  dek: '',
  body_markdown: '',
  cover_image_url: '',
  tags: [],
  cluster: 'ai-assisted-novel-writing',
  faq: [],
  related_slugs: [],
  author_name: 'Crown & Rose Editorial Team',
  author_bio: '',
  author_sameas: [],
  meta_description: '',
  meta_keywords: '',
  status: 'draft',
};

const LOCALE_FLAG = {
  en: '🇬🇧', fr: '🇫🇷', de: '🇩🇪', es: '🇪🇸', pt: '🇧🇷', zh: '🇨🇳',
};

const BLOG_TOPICS = [
  { key: 'writing-tools',            label: 'Writing Tools for Indie Authors' },
  { key: 'using-ai-to-draft-novels', label: 'Using AI to Draft Novels' },
  { key: 'book-sales-and-royalties', label: 'Book Sales & Royalties' },
  { key: 'writing-market-data',      label: 'Writing Market Data 2025-2026' },
  { key: 'indie-author-success',     label: 'Indie Author Success' },
  { key: 'loom-for-plotting',        label: 'Using Loom for Plotting' },
  { key: 'author-brand-building',    label: 'Author Brand Building' },
  { key: 'kdp-pricing-strategy',     label: 'KDP Pricing Strategy' },
];

const LOCALE_OPTIONS = [
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'pt', flag: '🇧🇷', name: 'Português' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
];

function errorMessage(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || String(d)).join('; ') || fallback;
  return fallback;
}

export default function BlogAdminPanel() {
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState(EMPTY_POST);
  const [busy, setBusy] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [relatedInput, setRelatedInput] = useState('');
  const [faqQ, setFaqQ] = useState('');
  const [faqA, setFaqA] = useState('');
  // Auto-generate state
  const [autoTopic, setAutoTopic] = useState('writing-tools');
  const [autoLocale, setAutoLocale] = useState('en');
  const [autoAllLocales, setAutoAllLocales] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [batchResults, setBatchResults] = useState(null);   // per-locale results after all-locales run
  const reload = async () => {
    try {
      const r = await adminApi.listBlogPosts();
      setPosts(r.posts || []);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not load blog posts.'));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const autoGenerate = async () => {
    setAutoGenerating(true);
    setBatchResults(null);
    try {
      const result = await adminApi.autoGenerateBlogPost({
        locale: autoLocale,
        topic_key: autoTopic,
        all_locales: autoAllLocales,
      });
      if (result.results) {
        setBatchResults(result);
        const ok = result.results.filter(r => r.ok).length;
        toast.success(`Auto-generated ${ok}/${result.total} posts across all locales.`);
      } else {
        toast.success(`Post "${result.post?.title}" published.`, {
          description: `Slug: ${result.post?.slug}`,
        });
        setDraft(result.post);
      }
      await reload();
    } catch (e) {
      toast.error('Auto-generation failed.', { description: errorMessage(e, 'Claude Haiku error.') });
    } finally {
      setAutoGenerating(false);
    }
  };

  const selectPost = (p) => setDraft({ ...EMPTY_POST, ...p });
  const newPost = () => setDraft(EMPTY_POST);

  const save = async (status) => {
    setBusy(true);
    try {
      const payload = { ...draft, status: status || draft.status };
      delete payload.id;
      delete payload.reading_minutes;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.published_at;
      let saved;
      if (draft.id) {
        saved = await adminApi.updateBlogPost(draft.id, payload);
      } else {
        saved = await adminApi.createBlogPost(payload);
      }
      toast.success(`Post ${saved.status === 'published' ? 'published' : 'saved as draft'}.`);
      setDraft(saved);
      await reload();
    } catch (e) {
      toast.error(errorMessage(e, 'Save failed.'));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!draft.id) return;
    if (!confirm(`Delete "${draft.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.deleteBlogPost(draft.id);
      toast.success('Post deleted.');
      newPost();
      await reload();
    } catch (e) {
      toast.error(errorMessage(e, 'Delete failed.'));
    } finally { setBusy(false); }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || draft.tags.includes(t)) return;
    setDraft((d) => ({ ...d, tags: [...d.tags, t] }));
    setTagInput('');
  };
  const removeTag = (t) => setDraft((d) => ({ ...d, tags: d.tags.filter((x) => x !== t) }));

  const addRelated = () => {
    const s = relatedInput.trim().toLowerCase();
    if (!s || draft.related_slugs.includes(s)) return;
    setDraft((d) => ({ ...d, related_slugs: [...d.related_slugs, s] }));
    setRelatedInput('');
  };
  const removeRelated = (s) => setDraft((d) => ({ ...d, related_slugs: d.related_slugs.filter((x) => x !== s) }));

  const addFaq = () => {
    if (faqQ.trim().length < 4 || faqA.trim().length < 10) {
      toast.error('Question needs 4+ characters and answer needs 10+ characters.');
      return;
    }
    setDraft((d) => ({ ...d, faq: [...d.faq, { question: faqQ.trim(), answer: faqA.trim() }] }));
    setFaqQ(''); setFaqA('');
  };
  const removeFaq = (i) => setDraft((d) => ({ ...d, faq: d.faq.filter((_, idx) => idx !== i) }));

  return (
    <section data-testid="admin-blog-panel">
      <PanelHeader
        title="Blog · Topical Authority"
        subtitle="Create and publish posts for the AI-assisted-novel-writing topic cluster. Published posts appear at /blog, are added to sitemap.xml, and carry Article + FAQPage JSON-LD automatically."
        onRefresh={reload}
      />

      {/* ── Auto-generate with Claude Haiku ── */}
      <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="blog-auto-generate-panel">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-amber-500" />
          <span className="text-[12px] font-semibold">AI Auto-Generate (Claude Haiku)</span>
          <span className="ml-auto text-[10px] text-muted-foreground">Daily cron runs at 07:00 UTC · rotates through all topics</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Topic</label>
            <select
              value={autoTopic}
              onChange={e => setAutoTopic(e.target.value)}
              className="h-8 rounded border border-border bg-background px-2 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="blog-auto-topic-select"
            >
              {BLOG_TOPICS.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide">Language</label>
            <select
              value={autoLocale}
              onChange={e => setAutoLocale(e.target.value)}
              disabled={autoAllLocales}
              className="h-8 rounded border border-border bg-background px-2 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              data-testid="blog-auto-locale-select"
            >
              {LOCALE_OPTIONS.map(l => (
                <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={autoAllLocales}
              onChange={e => setAutoAllLocales(e.target.checked)}
              className="h-3.5 w-3.5 rounded"
              data-testid="blog-auto-all-locales-check"
            />
            <Globe className="h-3 w-3" /> All 6 locales
          </label>
          <Button
            onClick={autoGenerate}
            disabled={autoGenerating}
            className="h-8 gap-1.5"
            data-testid="blog-auto-generate-btn"
          >
            {autoGenerating
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
              : <><Sparkles className="h-3.5 w-3.5" /> Generate &amp; Publish</>}
          </Button>
        </div>
      </div>

      {/* ── Batch generation results table ── */}
      {batchResults && (
        <div className="mt-3 rounded-md border border-border overflow-hidden" data-testid="blog-batch-results">
          <div className="flex items-center justify-between bg-muted/50 px-3 py-2 border-b border-border">
            <span className="text-[11px] font-semibold">
              Generation results — {batchResults.ok}/{batchResults.total} succeeded
            </span>
            <button
              type="button"
              onClick={() => setBatchResults(null)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="blog-batch-results-close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground w-8">Locale</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Title / Slug</th>
                <th className="px-3 py-1.5 text-center font-medium text-muted-foreground w-16">Status</th>
              </tr>
            </thead>
            <tbody>
              {batchResults.results.map((row, i) => (
                <tr key={row.locale}
                    className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}
                    data-testid={`blog-batch-row-${row.locale}`}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      <span className="text-base">{LOCALE_FLAG[row.locale] || '🌐'}</span>
                      <span className="font-mono text-[10.5px] text-muted-foreground">{row.locale}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-[280px]">
                    {row.ok ? (
                      <div>
                        <p className="font-medium truncate" title={row.title}>{row.title}</p>
                        <a
                          href={`/${row.locale === 'en' ? '' : row.locale + '/'}blog/${row.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-0.5 text-[10px] text-primary hover:underline mt-0.5"
                        >
                          <ExternalLink className="h-2.5 w-2.5" />
                          {row.slug}
                        </a>
                      </div>
                    ) : (
                      <span className="text-destructive text-[11px]">{row.error || 'Failed'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.ok
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
                      : <XCircle className="h-4 w-4 text-destructive mx-auto" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Backfill utilities ── */}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 text-[11px]"
          data-testid="blog-backfill-covers-btn"
          onClick={async () => {
            try {
              const r = await adminApi.backfillBlogCovers();
              toast.success(`Backfill started — ${r.queued} posts queued for cover generation.`);
            } catch (e) {
              toast.error('Backfill failed.', { description: e?.response?.data?.detail || String(e) });
            }
          }}
        >
          <Sparkles className="h-3 w-3" /> Backfill covers (existing posts)
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 text-[11px]"
          data-testid="blog-backfill-locale-links-btn"
          onClick={async () => {
            try {
              const r = await adminApi.backfillBlogLocaleLinks();
              toast.success(`Locale links wired — ${r.posts_updated} posts updated.`);
            } catch (e) {
              toast.error('Locale link backfill failed.', { description: e?.response?.data?.detail || String(e) });
            }
          }}
        >
          <Globe className="h-3 w-3" /> Backfill locale hreflang links
        </Button>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* ---------------- Post list ---------------- */}
        <div className="rounded-md border border-border bg-card p-3">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-semibold flex items-center gap-1.5"><Newspaper className="h-3.5 w-3.5" /> Posts ({posts.length})</div>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={newPost} data-testid="blog-new-post-btn">
              <Plus className="h-3 w-3 mr-1" /> New
            </Button>
          </div>
          <div className="mt-2 space-y-1 max-h-[60vh] overflow-y-auto">
            {posts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => selectPost(p)}
                data-testid={`blog-post-row-${p.slug}`}
                className={`w-full text-left rounded-sm px-2 py-1.5 text-[12px] hover:bg-muted ${draft.id === p.id ? 'bg-muted font-semibold' : ''}`}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {/* Locale flag badge — helps editors spot language at a glance */}
                  {p.locale && p.locale !== 'en' && (
                    <span
                      className="shrink-0 rounded px-1 py-0 text-[9px] font-bold uppercase tracking-wider bg-muted-foreground/15 text-muted-foreground"
                      data-testid={`blog-post-locale-badge-${p.slug}`}
                      title={p.locale}
                    >
                      {LOCALE_FLAG[p.locale] || p.locale}
                    </span>
                  )}
                  <span className="truncate">{p.title}</span>
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <span className={p.status === 'published' ? 'text-emerald-600' : 'text-amber-600'}>{p.status}</span>
                  {p.auto_generated && <span className="text-sky-500" title="AI-generated">AI</span>}
                  · <span className="truncate">{p.slug}</span>
                </div>
              </button>
            ))}
            {posts.length === 0 && <div className="text-[11px] text-muted-foreground px-2 py-2">No posts yet.</div>}
          </div>
        </div>

        {/* ---------------- Editor ---------------- */}
        <div className="rounded-md border border-border bg-card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-semibold">{draft.id ? 'Edit post' : 'New post'}</div>
            {draft.id && (
              <a href={`/blog/${draft.slug}`} target="_blank" rel="noreferrer" className="text-[11px] inline-flex items-center gap-1 text-primary hover:underline" data-testid="blog-view-live-link">
                View live <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          <div>
            <Label className="text-[11px]">Title</Label>
            <Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} data-testid="blog-title-input" />
          </div>
          <div>
            <Label className="text-[11px]">Slug (leave blank to auto-generate from title)</Label>
            <Input value={draft.slug} onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))} data-testid="blog-slug-input" />
          </div>
          <div>
            <Label className="text-[11px]">Dek / excerpt</Label>
            <Textarea rows={2} value={draft.dek} onChange={(e) => setDraft((d) => ({ ...d, dek: e.target.value }))} data-testid="blog-dek-input" />
          </div>
          <div>
            <Label className="text-[11px]">Body (Markdown)</Label>
            <Textarea rows={14} className="font-mono text-[12px]" value={draft.body_markdown} onChange={(e) => setDraft((d) => ({ ...d, body_markdown: e.target.value }))} data-testid="blog-body-input" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-[11px]">Cover image URL</Label>
              <Input value={draft.cover_image_url || ''} onChange={(e) => setDraft((d) => ({ ...d, cover_image_url: e.target.value }))} data-testid="blog-cover-input" />
            </div>
            <div>
              <Label className="text-[11px]">Cluster</Label>
              <Input value={draft.cluster} onChange={(e) => setDraft((d) => ({ ...d, cluster: e.target.value }))} data-testid="blog-cluster-input" />
            </div>
          </div>

          <div>
            <Label className="text-[11px]">Tags</Label>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="e.g. mixed-llm" data-testid="blog-tag-input" />
              <Button type="button" size="sm" variant="outline" onClick={addTag} data-testid="blog-tag-add-btn">Add</Button>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {draft.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px]" data-testid={`blog-tag-chip-${t}`}>
                  {t} <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(t)} />
                </span>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[11px]">Related post slugs (interlinking)</Label>
            <div className="flex gap-2">
              <Input value={relatedInput} onChange={(e) => setRelatedInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRelated())} placeholder="slug-of-other-post" data-testid="blog-related-input" />
              <Button type="button" size="sm" variant="outline" onClick={addRelated} data-testid="blog-related-add-btn">Add</Button>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {draft.related_slugs.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px]" data-testid={`blog-related-chip-${s}`}>
                  {s} <X className="h-3 w-3 cursor-pointer" onClick={() => removeRelated(s)} />
                </span>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[11px]">FAQ (powers FAQPage schema)</Label>
            <div className="space-y-2">
              {draft.faq.map((f, i) => (
                <div key={i} className="flex items-start justify-between gap-2 rounded-sm border border-border p-2 text-[11px]" data-testid={`blog-faq-item-${i}`}>
                  <div><strong>{f.question}</strong><div className="text-muted-foreground">{f.answer}</div></div>
                  <Trash2 className="h-3.5 w-3.5 cursor-pointer shrink-0" onClick={() => removeFaq(i)} />
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1.5">
              <Input value={faqQ} onChange={(e) => setFaqQ(e.target.value)} placeholder="Question" data-testid="blog-faq-question-input" />
              <Textarea rows={2} value={faqA} onChange={(e) => setFaqA(e.target.value)} placeholder="Answer" data-testid="blog-faq-answer-input" />
              <Button type="button" size="sm" variant="outline" onClick={addFaq} data-testid="blog-faq-add-btn"><Plus className="h-3 w-3 mr-1" /> Add FAQ item</Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-[11px]">Meta description (SEO)</Label>
              <Textarea rows={2} value={draft.meta_description} onChange={(e) => setDraft((d) => ({ ...d, meta_description: e.target.value }))} data-testid="blog-meta-description-input" />
            </div>
            <div>
              <Label className="text-[11px]">Meta keywords (comma-separated)</Label>
              <Textarea rows={2} value={draft.meta_keywords} onChange={(e) => setDraft((d) => ({ ...d, meta_keywords: e.target.value }))} data-testid="blog-meta-keywords-input" />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-[11px]">Author name</Label>
              <Input value={draft.author_name} onChange={(e) => setDraft((d) => ({ ...d, author_name: e.target.value }))} data-testid="blog-author-name-input" />
            </div>
            <div>
              <Label className="text-[11px]">Author sameAs URLs (comma-separated)</Label>
              <Input
                value={(draft.author_sameas || []).join(', ')}
                onChange={(e) => setDraft((d) => ({ ...d, author_sameas: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))}
                data-testid="blog-author-sameas-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Author bio</Label>
            <Textarea rows={2} value={draft.author_bio} onChange={(e) => setDraft((d) => ({ ...d, author_bio: e.target.value }))} data-testid="blog-author-bio-input" />
          </div>

          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="flex gap-2">
              {draft.id && (
                <Button type="button" size="sm" variant="outline" className="text-destructive" onClick={remove} data-testid="blog-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => save('draft')} data-testid="blog-save-draft-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save draft
              </Button>
              <Button type="button" size="sm" disabled={busy} onClick={() => save('published')} data-testid="blog-publish-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Newspaper className="h-3.5 w-3.5 mr-1" />} Publish
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
